import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import type { DeviceViewSettingsTable, NodeType } from '@ipfabric/utils';
import {
  cloudInstanceNodeTypes,
  cloudInternetGwNodeTypes,
  cloudNatGwNodeTypes,
  cloudNetworkInterfaceNodeTypes,
  cloudNetworkNodeTypes,
  cloudPeeringNodeTypes,
  cloudSubnetNodeTypes,
  isCloudNetworkNodeType,
} from '@ipfabric/utils';
import type { PathsWithName } from 'components/DeviceView/deviceViewTypes';

// Per-cloud-construct default tabs (NIM-24243). Each of the 12
// cloud-construct node types (3 vendors x 4 kinds) maps to the same ordered
// list of cloud `webEndpoint`s for its kind. We keep these as just endpoints
// — without labels — and resolve the human-readable label at runtime from
// the OAS via `pathsWithName` (see `getCloudConstructDefaultTables`). That
// way the dialog title stays in sync with the YAML's `post.summary` /
// `x-table.title` (e.g. "Cloud Networks Inventory") without having to keep
// a parallel hardcoded copy here.
const cloudNetworkEndpoints = [
  '/technology/cloud/subnets/inventory',
  '/technology/cloud/route-tables/inventory',
  '/technology/cloud/endpoints/virtual-machines',
  '/technology/cloud/network-interfaces',
];

const cloudNetworkInterfaceEndpoints = [
  '/technology/cloud/subnets/inventory',
  '/technology/cloud/networks/inventory',
  '/technology/cloud/route-tables/inventory',
];

const cloudSubnetEndpoints = [
  '/technology/cloud/networks/inventory',
  '/technology/cloud/network-interfaces',
  '/technology/cloud/route-tables/inventory',
];

const cloudPeeringEndpoints = [
  '/technology/cloud/networks/peerings',
  '/technology/cloud/networks/inventory',
];

const cloudInstanceEndpoints = [
  '/technology/cloud/endpoints/virtual-machines',
  '/technology/cloud/network-interfaces',
];

const cloudNatGwEndpoints = ['/technology/cloud/networks/inventory'];

// The rules table is GCP-only, so only GCP Cloud NAT gets the Rules tab.
const gcpCloudNatEndpoints = [
  '/technology/cloud/nat-gateways/rules',
  ...cloudNatGwEndpoints,
];

const cloudInternetGwEndpoints = ['/technology/cloud/networks/inventory'];

const cloudConstructDefaultEndpoints: { [nodeType: string]: string[] } = {
  ...Object.fromEntries(
    cloudNetworkNodeTypes.map((nodeType) => [nodeType, cloudNetworkEndpoints]),
  ),
  ...Object.fromEntries(
    cloudNetworkInterfaceNodeTypes.map((nodeType) => [
      nodeType,
      cloudNetworkInterfaceEndpoints,
    ]),
  ),
  ...Object.fromEntries(
    cloudSubnetNodeTypes.map((nodeType) => [nodeType, cloudSubnetEndpoints]),
  ),
  ...Object.fromEntries(
    cloudPeeringNodeTypes.map((nodeType) => [nodeType, cloudPeeringEndpoints]),
  ),
  ...Object.fromEntries(
    cloudInstanceNodeTypes.map((nodeType) => [
      nodeType,
      cloudInstanceEndpoints,
    ]),
  ),
  ...Object.fromEntries(
    cloudNatGwNodeTypes.map((nodeType) => [
      nodeType,
      nodeType === 'gcpCloudNat' ? gcpCloudNatEndpoints : cloudNatGwEndpoints,
    ]),
  ),
  ...Object.fromEntries(
    cloudInternetGwNodeTypes.map((nodeType) => [
      nodeType,
      cloudInternetGwEndpoints,
    ]),
  ),
};

/**
 * Returns the design-defined cloud tabs for a cloud-construct node type
 * (NIM-24243), with labels resolved from the OAS (`x-table.title` / route
 * `post.summary`) via the `pathsWithName` lookup the device-explorer
 * already builds. Returns `undefined` for non-cloud node types, and for
 * cloud-network node types (awsVpc/azureVnet/gcpVpc) while
 * ENABLE_NEW_CLOUD_MODEL is disabled (NIM-24373) so callers fall back to
 * the old device tables.
 *
 * Labels are intentionally NOT hardcoded here so renaming a cloud table in
 * its YAML automatically flows into the device-explorer tabs.
 */
export const getCloudConstructDefaultTables = (
  nodeType: NodeType,
  pathsWithName: PathsWithName,
): DeviceViewSettingsTable[] | undefined => {
  const endpoints = cloudConstructDefaultEndpoints[nodeType];
  if (
    endpoints === undefined ||
    (isCloudNetworkNodeType(nodeType) &&
      !isFeatureFlagEnabled('ENABLE_NEW_CLOUD_MODEL'))
  ) {
    return undefined;
  }
  return endpoints.map((webEndpoint) => ({
    // Fall back to the webEndpoint itself when the OAS has no entry for it
    // (e.g. a feature-flag-gated route) so the tab still renders something
    // identifiable rather than an empty string.
    label: pathsWithName[webEndpoint] ?? webEndpoint,
    webEndpoint,
  }));
};

export const customTableSettingsNodeTypes = [
  'fex',
  'host',
  'hostGroup',
  'phone',
  'phoneGroup',
  ...cloudNetworkNodeTypes,
  ...cloudNetworkInterfaceNodeTypes,
  ...cloudSubnetNodeTypes,
  ...cloudPeeringNodeTypes,
  ...cloudInstanceNodeTypes,
  ...cloudNatGwNodeTypes,
  ...cloudInternetGwNodeTypes,
] satisfies NodeType[];
export type CustomTableSettingsNodeType =
  (typeof customTableSettingsNodeTypes)[number];

export const hasNodeCustomTableSettings = (nodeType: NodeType): boolean =>
  (customTableSettingsNodeTypes as NodeType[]).includes(nodeType) &&
  (!isCloudNetworkNodeType(nodeType) ||
    isFeatureFlagEnabled('ENABLE_NEW_CLOUD_MODEL'));

type CustomNodeTypeTableSettings = {
  [NodeType in CustomTableSettingsNodeType]?: DeviceViewSettingsTable[];
};

// On-prem custom tables — labels are still hardcoded here because they
// don't match the YAML's vendor-prefixed `summary` (e.g. "Cisco FEX
// Modules" vs the UI's "FEX Modules").
export const customNodeTypeTableSettings: CustomNodeTypeTableSettings = {
  fex: [
    {
      label: 'FEX Modules',
      webEndpoint: '/technology/platforms/fex/fex-modules',
    },
    {
      label: 'FEX Interfaces',
      webEndpoint: '/technology/platforms/fex/fex-ints',
    },
  ],
  host: [
    {
      label: 'Hosts',
      webEndpoint: '/inventory/hosts',
    },
  ],
  hostGroup: [
    {
      label: 'Hosts',
      webEndpoint: '/inventory/hosts',
    },
  ],
  phone: [
    {
      label: 'Hosts',
      webEndpoint: '/inventory/hosts',
    },
  ],
  phoneGroup: [
    {
      label: 'Hosts',
      webEndpoint: '/inventory/hosts',
    },
  ],
};
